import { useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { downloadRecentContents } from "state/content/operations";
import { requestToDownloadContents } from "state/content/utils";
import type { RootState } from "state/rootReducer";
import { NewStockExchangeRecord, StockExchangeRecord } from "types/stock";
import type { ContentDetails } from "types/content/ContentDetails";
import { useCollectionKey, useCollectionStock } from "./useCollections";
import { useConversationCompanyExchangeValue, useGetConversationCompanyTickerValue } from "./useConversation";
import { useCollectionMetadata, useCollectionMetadataIds } from "./useCollectionMetadata";
import { getTickersQuery } from "api/tickers";
import { ManualTags, hasProjectOutput } from "screens/collection/components/utils";
import type { DashboardMetricsGroupType, DashboardMetricsData, DashboardMetric } from "types/dashboardMetrics";

// Global map to track requested IDs with their request context
const globalRequestedIds = new Map<string, Set<string>>();

// Global map to track request timestamps for cleanup
const globalRequestTimestamps = new Map<string, number>();

// Timeout for request cleanup (5 minutes)
const REQUEST_TIMEOUT = 5 * 60 * 1000;

// Function to create a unique request key based on options
function createRequestKey(options: { refreshFromNetwork: boolean; filterByTags?: string[]; filterByName?: string }) {
  const tags = options.filterByTags?.sort().join(",") || "no-tags";
  const name = options.filterByName || "no-name";
  return `refresh:${options.refreshFromNetwork}-tags:${tags}-name:${name}`;
}

// Function to clear global state (useful for testing/debugging)
export function clearGlobalRequestState() {
  globalRequestedIds.clear();
  globalRequestTimestamps.clear();
}

export type GroupedContentDetails = {
  [name: string]: ContentDetails[];
};

export function useContentDetails(metadataId?: string, options = { refreshFromNetwork: false }) {
  const content = useSelector((state: RootState) => (metadataId ? state.content.contentData[metadataId] : undefined), shallowEqual);
  const hasContent = useSelector((state: RootState) => !!metadataId && !!state.content.contentData[metadataId], shallowEqual);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!hasContent && metadataId && options.refreshFromNetwork) {
      requestToDownloadContents({ metadataIds: [metadataId] }, dispatch);
    }
  }, [dispatch, metadataId, hasContent, options.refreshFromNetwork]);

  return content;
}

export function useAllContents() {
  return useSelector((state: RootState) => state.content.contentData, shallowEqual);
}

export function useManualTags() {
  return useMemo(() => [ManualTags.projectOutput], []);
}

export function useContents(
  metadataIds: string[] | undefined,
  options: { refreshFromNetwork: boolean; filterByTags?: string[]; filterByName?: string } = {
    refreshFromNetwork: false,
    filterByTags: undefined,
    filterByName: undefined,
  }
) {
  // TODO: when backend supports name filtering update this function to support it
  const deDuplicatedMetadataIds = useMemo(() => [...new Set(metadataIds)], [metadataIds]);
  const contentData = useSelector((state: RootState) => state.content.contentData, shallowEqual);
  const isLoading = useSelector((state: RootState) => state.content.isLoading, shallowEqual);

  const content = useMemo(() => {
    if (!deDuplicatedMetadataIds) return [];

    const mappedContent = deDuplicatedMetadataIds.map((id) => contentData[id]);
    const existingContent = mappedContent.filter((contentItem): contentItem is ContentDetails => !!contentItem);

    const filteredContent = existingContent.filter((contentItem) => {
      // Filter by tags
      if (options.filterByTags && options.filterByTags.length > 0) {
        const allTags = [...contentItem.manualTags, ...contentItem.autoTags];
        const hasMatchingTag = options.filterByTags.some((tag) => allTags.includes(tag));
        if (!hasMatchingTag) return false;
      }

      // Filter by name
      if (options.filterByName && options.filterByName.trim() !== "") {
        const contentName = contentItem.name?.toLowerCase() || "";
        const searchName = options.filterByName.toLowerCase().trim();
        if (!contentName.includes(searchName)) return false;
      }

      return true;
    });

    return filteredContent;
  }, [deDuplicatedMetadataIds, contentData, options.filterByTags, options.filterByName]);

  const missingMetadataIds = useMemo(() => {
    if (!deDuplicatedMetadataIds) return [];
    return deDuplicatedMetadataIds.filter((id) => !contentData[id]);
  }, [deDuplicatedMetadataIds, contentData]);

  // Check if we already have content that matches the filter criteria
  const hasMatchingFilteredContent = useMemo(() => {
    if ((!options.filterByTags || options.filterByTags.length === 0) && (!options.filterByName || options.filterByName.trim() === ""))
      return false;

    return content.length > 0;
  }, [content, options.filterByTags, options.filterByName]);

  const dispatch = useDispatch();

  useEffect(() => {
    // Don't download more content if we already have content matching the filter tags
    if (options.refreshFromNetwork && missingMetadataIds.length > 0 && !isLoading && !hasMatchingFilteredContent) {
      const now = Date.now();
      const requestKey = createRequestKey(options);

      // Clean up expired requests
      globalRequestTimestamps.forEach((requestTime, id) => {
        if (now - requestTime > REQUEST_TIMEOUT) {
          globalRequestTimestamps.delete(id);
          // Clean up from all request contexts
          globalRequestedIds.forEach((contextSet) => {
            contextSet.delete(id);
          });
        }
      });

      // Get or create the set for this request context
      if (!globalRequestedIds.has(requestKey)) {
        globalRequestedIds.set(requestKey, new Set());
      }
      const contextRequestedIds = globalRequestedIds.get(requestKey)!;

      // Filter out IDs that are already being requested in this context (and not expired)
      const newIdsToRequest = missingMetadataIds.filter((id) => {
        if (!contextRequestedIds.has(id)) return true;

        const requestTime = globalRequestTimestamps.get(id);
        return !requestTime || now - requestTime > REQUEST_TIMEOUT;
      });

      if (newIdsToRequest.length > 0) {
        // Mark these IDs as being requested in this context with timestamp
        newIdsToRequest.forEach((id) => {
          contextRequestedIds.add(id);
          globalRequestTimestamps.set(id, now);
        });

        requestToDownloadContents({ metadataIds: newIdsToRequest, ...(options.filterByTags && { tags: options.filterByTags }) }, dispatch);
      }
    }
  }, [dispatch, missingMetadataIds, options.refreshFromNetwork, options.filterByTags, isLoading, options, hasMatchingFilteredContent]);

  // Clean up requested IDs when content is loaded or component unmounts
  useEffect(() => {
    const currentMissingSet = new Set(missingMetadataIds);
    const requestKey = createRequestKey(options);
    const contextRequestedIds = globalRequestedIds.get(requestKey);

    if (contextRequestedIds) {
      contextRequestedIds.forEach((id) => {
        if (!currentMissingSet.has(id) && contentData[id]) {
          contextRequestedIds.delete(id);

          // If no other contexts are tracking this ID, clean up the timestamp
          const isTrackedElsewhere = Array.from(globalRequestedIds.values()).some(
            (otherSet) => otherSet !== contextRequestedIds && otherSet.has(id)
          );
          if (!isTrackedElsewhere) {
            globalRequestTimestamps.delete(id);
          }
        }
      });
    }
  }, [missingMetadataIds, contentData, options]);

  // Always return the content array, even if empty - this allows components to distinguish between "no content" and "loading"
  return content;
}

export function useFilterAndGroupReports(contentData: ContentDetails[] | undefined): GroupedContentDetails {
  return useMemo(() => {
    if (!contentData) return {};

    const filteredContent = contentData.filter(hasProjectOutput);
    const sorted = filteredContent.sort(
      (a: ContentDetails, b: ContentDetails) => new Date(a.timeCreated).getTime() - new Date(b.timeCreated).getTime()
    );

    return sorted.reduce((acc: GroupedContentDetails, content) => {
      const nameParts = content.name ? content.name.split(" - ") : ["AI Generated Report"];
      const name = nameParts.slice(0, -1).join(" : ").trim() || "AI Generated Report";

      if (!acc[name]) {
        acc[name] = [];
      }
      acc[name].push(content);
      return acc;
    }, {});
  }, [contentData]);
}

export function useCompanyStockFromCollectionOrContents(collectionId: string | undefined): StockExchangeRecord | null {
  const metadataIds = useCollectionMetadataIds(collectionId);
  const collectionStock = useCollectionStock(collectionId);
  const maybeContentsStock = useSelector((state: RootState) => {
    if (collectionStock) {
      return collectionStock;
    }

    const contents = metadataIds.flatMap((metadataId) =>
      state.collectionMetadata.contentsByIds[metadataId] ? [state.collectionMetadata.contentsByIds[metadataId]] : []
    );

    const companyDetailsContent = contents.find((content) => content.dynamicData?.category === "company_details");

    if (!companyDetailsContent) {
      return null;
    }

    const validStockExchange = StockExchangeRecord.validate(companyDetailsContent.dynamicData?.dynamic_data);
    const validNewStockExchange = NewStockExchangeRecord.validate(companyDetailsContent.dynamicData?.dynamic_data);

    if (validStockExchange.success) {
      return {
        name: validStockExchange.value.name,
        exchange: validStockExchange.value.exchange,
        ticker: validStockExchange.value.ticker,
      };
    } else if (validNewStockExchange.success) {
      return {
        name: validNewStockExchange.value.company_name,
        exchange: validNewStockExchange.value.company_stock_exchange,
        ticker: validNewStockExchange.value.company_ticker,
      };
    } else {
      return null;
    }
  }, shallowEqual);

  return collectionStock || maybeContentsStock;
}

export function useCompanyStockFromCollectionContentsOrConversation(collectionId: string | undefined): StockExchangeRecord | null {
  const collectionConversationId = useCollectionKey(collectionId, "conversationId");
  const getConversationTickerValue = useGetConversationCompanyTickerValue();
  const conversationExchange = useConversationCompanyExchangeValue(collectionConversationId);
  const collectionStockData = useCompanyStockFromCollectionOrContents(collectionId);
  const [tickerData, setTickerData] = useState<StockExchangeRecord | null>(null);

  useEffect(() => {
    if (collectionStockData?.ticker) {
      setTickerData(collectionStockData);
      return;
    }

    const conversationTicker = collectionConversationId && getConversationTickerValue(collectionConversationId);

    if (!conversationTicker || !conversationExchange) {
      setTickerData(null);
      return;
    }

    let isCurrent = true;
    getTickersQuery({ limit: 1, query: conversationTicker, exchange: conversationExchange })
      .then((response) => {
        if (!isCurrent) return;
        if (response && response.length > 0) {
          setTickerData({
            name: response[0].name,
            exchange: response[0].exchange,
            ticker: response[0].symbol,
          });
        } else {
          setTickerData(null);
        }
      })
      .catch((error) => {
        console.error(error);
        if (isCurrent) setTickerData(null);
      });

    return () => {
      isCurrent = false;
    };
  }, [collectionConversationId, conversationExchange, collectionStockData, getConversationTickerValue]);

  return collectionStockData || tickerData;
}

export function useContent(metadataId: string): ContentDetails | undefined {
  return useSelector((state: RootState) => state.content.contentData[metadataId], shallowEqual);
}

export function useContentFromCollectionOrContents(metadataId: string): ContentDetails | Partial<ContentDetails> | undefined {
  const maybeCollectionMetadata = useCollectionMetadata(metadataId);
  const maybeContentData = useContent(metadataId);

  return useMemo(() => maybeCollectionMetadata || maybeContentData, [maybeCollectionMetadata, maybeContentData]);
}

export function useRecentContents(limit: number) {
  const contents = useSelector((state: RootState) => {
    return state.content.order.slice(0, limit).map((metadataId) => state.content.contentData[metadataId]);
  }, shallowEqual);

  return contents;
}

export function useTotalContentsCount() {
  return useSelector((state: RootState) => state.content.recentContent.totalCount);
}

export function useDownloadRecentContents(limit: number) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(downloadRecentContents({ limit: limit }));
  }, [dispatch, limit]);
}

export const useLoadingContents = (): boolean => {
  return useSelector((state: RootState) => state.content.isLoading);
};

export function useDashboardMetrics(
  metadataIds: string[] | undefined,
  group?: DashboardMetricsGroupType
): DashboardMetricsData | DashboardMetric[] | undefined {
  const dashboardMetricsContent = useContents(metadataIds, {
    refreshFromNetwork: true,
    filterByTags: ["dashboard_metrics"],
  });

  return useMemo(() => {
    const data = dashboardMetricsContent?.[0]?.dynamicData?.dynamic_data as DashboardMetricsData | undefined;

    if (!data || !Array.isArray(data)) return undefined;

    if (group) {
      const groupData = data.find((item) => item.group === group);
      return groupData?.metrics;
    }

    return data;
  }, [dashboardMetricsContent, group]);
}
